import React from "react";
import { motion } from "framer-motion";
import { Target, Compass } from "lucide-react";

const CARDS = [
  {
    eyebrow: "Our Mission",
    icon: Target,
    title: "Building Reliability Through Engineering",
    body: "To manufacture premium construction machinery that delivers dependable performance, uncompromising safety and long-term value while earning the trust of every customer.",
  },
  {
    eyebrow: "Our Vision",
    icon: Compass,
    title: "Engineering the Future of Construction",
    body: "To become one of the world's most trusted and recommended construction machinery manufacturers through engineering excellence, continuous innovation and exceptional customer satisfaction.",
  },
];

export default function MissionVisionSection() {
  return (
    <section
      data-testid="about-mission-vision"
      className="relative bg-liftile-cream py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      {/* Soft orange ambient */}
      <div
        aria-hidden
        className="absolute -top-32 right-0 w-[460px] h-[460px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.7) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-7 lg:gap-9">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.article
                key={card.eyebrow}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.65, ease: "easeOut", delay: i * 0.15 }}
                data-testid={`mv-card-${i}`}
                className="group relative bg-white rounded-2xl p-9 md:p-12 lg:p-14 border border-black/[0.04] shadow-[0_6px_24px_-12px_rgba(0,0,0,0.12)] hover:shadow-[0_30px_60px_-20px_rgba(255,107,26,0.4)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                {/* Decorative orange diagonal accent in corner */}
                <div
                  aria-hidden
                  className="absolute -top-12 -right-12 w-32 h-32 rotate-45 bg-liftile-orange/8 group-hover:bg-liftile-orange/20 transition-colors duration-500"
                />

                {/* Icon plate */}
                <div className="relative w-16 h-16 md:w-18 md:h-18 rounded-2xl grid place-items-center bg-liftile-orange/10 text-liftile-orange mb-8 group-hover:bg-liftile-orange group-hover:text-white transition-all duration-500">
                  <Icon className="w-8 h-8 md:w-9 md:h-9" strokeWidth={1.5} />
                </div>

                {/* Eyebrow */}
                <div className="font-heading text-[10px] md:text-xs uppercase tracking-[0.32em] text-liftile-orange font-semibold mb-3">
                  {card.eyebrow}
                </div>

                {/* Title */}
                <h3
                  data-testid={`mv-card-${i}-title`}
                  className="font-display uppercase text-2xl md:text-3xl lg:text-[2rem] text-liftile-matte-black leading-[1.05] tracking-tight"
                >
                  {card.title}
                </h3>

                {/* Orange accent line */}
                <div className="mt-5 w-12 h-0.5 bg-liftile-orange transition-all duration-500 group-hover:w-20" />

                {/* Body */}
                <p className="mt-6 font-body text-base md:text-[1.05rem] text-liftile-matte-black/75 leading-relaxed">
                  {card.body}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
